import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PaginationComponent from '../Pagination';
import NoDataComponent from '../NoData';
import TableComponent from '../Table';
import InputSearchComponent from '../Common/Form/input-search';
import { formatDate } from '../../utils/helper.util';
import ChevronDownIcon from '../../assets/chevron-down-green-icon.svg';
import EyeGreenIcon from '../../assets/eye-light-green-icon.svg';
import EyeDisabledIcon from '../../assets/eye-disabled-icon.svg';
import AvatarDefault from '../../assets/medium_default_optimized.png';
import NoResultImg from '../../assets/no-result.svg';
import {
  approveResponse,
  fetchListRequestResponses,
  fetchResponseContentTypes,
  rejectResponse
} from '../../apis/response.api';
import ToastBtnMessage from '../ToastBtnMessage';
import { useDebounce } from 'use-debounce';
import ViewResponseDetailModal from './modal/view-response-detail.component';
import ConfirmModal from './modal/confirm-modal.component.jsx';
import { useNavigate } from 'react-router-dom';
import { DataLayer } from '../../utils/data-layer';
import { useAtom } from 'jotai';
import { profileAtom } from '../Settings/atom';

export const SubmissionContainerComponent = ({ requestId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuRef = useRef();
  const [modals, setModals] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState({});
  const toast = useToast();
  const navigate = useNavigate();
  const [profile] = useAtom(profileAtom);

  const MODAL = {
    VIEW_DETAIL: 'view-detail',
    APPROVEMENT: 'approvement',
    REJECTION: 'rejection'
  };

  // custom range values
  const [minEquityPercentage, setMinEquityPercentage] = useState(undefined);
  const [maxEquityPercentage, setMaxEquityPercentage] = useState(undefined);
  const [debouncedMinEquity] = useDebounce(minEquityPercentage, 500);
  const [debouncedMaxEquity] = useDebounce(maxEquityPercentage, 500);

  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 5,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const [equityPercentage, setEquityPercentage] = useState({
    id: undefined,
    name: undefined,
    from: undefined,
    to: undefined
  });
  const [type, setType] = useState({
    id: undefined,
    name: undefined
  });
  const listEquityPercentage = [
    { id: undefined, name: 'Equity percentage', from: undefined, to: undefined },
    { id: 1, name: '0-5%', from: 0, to: 5 },
    { id: 2, name: '5-15%', from: 5, to: 15 },
    { id: 3, name: '10-15%', from: 10, to: 15 },
    { id: 4, name: '15-20%', from: 15, to: 20 },
    { id: 5, name: '>20%', from: 20, to: undefined },
    { id: 6, name: 'Custom range', from: undefined, to: undefined }
  ];

  // list requests params
  const params = {
    ...pagination,
    contentTypeIds: [type.id],
    equityPercentageFrom: equityPercentage.from,
    equityPercentageTo: equityPercentage.to
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const {
    data: getListResponses,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getListResponses', filteredParams],
    queryFn: () => fetchListRequestResponses(requestId, filteredParams)
  });

  const { data: getListTopic } = useQuery({
    queryFn: fetchResponseContentTypes
  });

  const { mutateAsync: approveResponseAsync } = useMutation({
    mutationFn: approveResponse
  });

  const { mutateAsync: rejectResponseAsync } = useMutation({
    mutationFn: rejectResponse
  });

  useEffect(() => {
    if (getListResponses) {
      setDataTable(getListResponses?.data);
    }
  }, [getListResponses, type.id]);

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (minEquityPercentage === undefined && maxEquityPercentage === undefined) return;
    setEquityPercentage({
      ...equityPercentage,
      from: minEquityPercentage || undefined,
      to: maxEquityPercentage || undefined
    });
  }, [debouncedMinEquity, debouncedMaxEquity]);

  const handleChangePage = (page) => {
    setDataTable(() => []);
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const handleChangeType = (item) => () => {
    setType(item);
    setPagination((pre) => {
      return {
        ...pre,
        page: 1
      };
    });
  };

  const handleChangeEquityPercentage = (item) => () => {
    setMinEquityPercentage(undefined);
    setMaxEquityPercentage(undefined);
    setEquityPercentage(item);
    setPagination((pre) => {
      return {
        ...pre,
        page: 1
      };
    });

    // Prevent the menu from closing
    if (item?.id !== 6) onClose();
  };

  const handleApproveResponse = async (id) => {
    try {
      // Track content accept event
      const response = selectedResponse;
      const contentType = response?.contentTypes?.[0]?.name || 'unknown';
      const username = profile?.username;

      DataLayer.trackContentAccept(contentType, username);

      await approveResponseAsync(id);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Response have been accepted'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      setModals([]);
      refetch();
    } catch (error) {
      console.error('Error approve response:', error);
      throw error;
    }
  };

  const handleRejectResponse = async (id) => {
    try {
      // Track content reject event
      const response = selectedResponse;
      const contentType = response?.contentTypes?.[0]?.name || 'unknown';
      const username = profile?.username;

      DataLayer.trackContentReject(contentType, username);

      await rejectResponseAsync(id);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Response have been rejected'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      setModals([]);
      refetch();
    } catch (error) {
      console.error('Error reject response:', error);
      throw error;
    }
  };

  const handleCloseModal = (modalName) => {
    setModals(modals.filter((modal) => modal !== modalName));
  };

  const handleShowApprovementModal = (data) => {
    setSelectedResponse(data);
    setModals([...modals, MODAL.APPROVEMENT]);
  };

  const handleShowRejectionModal = (data) => {
    setSelectedResponse(data);
    setModals([...modals, MODAL.REJECTION]);
  };

  const convertResponses = useCallback(
    (row) => {
      return (
        <Box display={'flex'} alignItems={'center'}>
          <Text
            fontWeight={600}
            fontSize={'xs'}
            title={row?.title}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
          >
            {row?.title}
          </Text>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertNotes = useCallback(
    (row) => {
      return (
        <Box display={'flex'} alignItems={'center'}>
          <Text
            fontWeight={400}
            fontSize={'xs'}
            title={row?.note}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
          >
            {row?.note}
          </Text>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertContributor = useCallback(
    (row) => {
      return (
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
          <img
            className="h-6 w-6 rounded-full object-cover cursor-pointer flex-shrink-0"
            src={row?.user?.imgUrl || AvatarDefault}
            alt=""
            onClick={() => navigate(`/${row?.user?.username}`)}
          />
          <Box display={'flex'} flexDir={'column'} minW={'0'}>
            <Text
              fontWeight={600}
              fontSize={'xs'}
              title={row?.user?.firstName}
              cursor={'pointer'}
              onClick={() => navigate(`/${row?.user?.username}`)}
              _hover={{
                color: '#4CAF50'
              }}
              transition={'0.2s'}
              isTruncated
            >
              {row?.user?.firstName}
            </Text>
            {row?.user?.username && (
              <span className="font-[600] text-[10px] leading-[12px] text-02-dim-gray">
                @{row?.user?.username}
              </span>
            )}
          </Box>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertEquityPercentage = useCallback(
    (row) => {
      return (
        <Box display={'flex'} alignItems={'center'}>
          <Text
            fontWeight={400}
            fontSize={'xs'}
            title={row?.title}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
          >
            {row?.equityPercentage}%
          </Text>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertType = useCallback((row) => {
    return (
      <Box>
        {row?.contentTypes?.map((item) => (
          <Box
            key={item.id}
            px={'8px'}
            py={'4px'}
            rounded={'80px'}
            bgColor={'#F0EDEC'}
            w={'fit-content'}
            whiteSpace={'nowrap'}
            display={'flex'}
            gap={2}
          >
            <Text title={item?.name} color="#6A4E42" fontWeight={800} fontSize={'xs'}>
              {item?.name}
            </Text>
          </Box>
        ))}
      </Box>
    );
  }, []);

  const convertStatus = useCallback((row) => {
    let text = '';
    let textColor = '';
    let bgColor = '';
    let fontWeight = 0;
    switch (row.status) {
      case 'rejected': {
        text = 'Rejected';
        textColor = '#FFFFFF';
        bgColor = '#DE1414';
        fontWeight = 800;
        break;
      }
      case 'approved': {
        text = 'Accepted';
        textColor = '#FFFFFF';
        bgColor = '#4CAF50';
        fontWeight = 800;
        break;
      }
      case 'pending': {
        text = 'Pending';
        textColor = '#204A22';
        bgColor = '#EDF7EE';
        fontWeight = 600;
        break;
      }
      default: {
      }
    }
    return (
      <Box py={'8px'} px={'12px'} rounded={'80px'} bgColor={bgColor} w={'fit-content'}>
        <Text color={textColor} fontSize={'xs'} fontWeight={fontWeight}>
          {text}
        </Text>
      </Box>
    );
  }, []);

  const convertAction = useCallback((row) => {
    return (
      <Box display="flex" gap="12px" alignItems={'center'} w="max-content">
        <img
          src={row.status === 'rejected' ? EyeDisabledIcon : EyeGreenIcon}
          className={`${row.status === 'rejected' ? 'cursor-not-allowed' : 'cursor-pointer'} p-[10px] hover:opacity-80`}
          alt="icon"
          title={
            row.status === 'rejected'
              ? 'View Disabled (Response Rejected)'
              : 'View Response Details'
          }
          onClick={() => {
            if (row.status !== 'rejected') {
              setSelectedResponse(row);
              setModals([MODAL.VIEW_DETAIL]);
            }
          }}
        />
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Responses',
        accessor: (row) => convertResponses(row),
        id: 'title',
        isSortField: true,
        widthCustom: '12rem',
        minWidthCustom: '12rem'
      },
      {
        Header: 'Notes',
        accessor: (row) => convertNotes(row),
        id: 'note',
        isSortField: false,
        widthCustom: '14rem',
        minWidthCustom: '14rem'
      },
      {
        Header: 'Contributor',
        accessor: (row) => convertContributor(row),
        id: 'contributor',
        isSortField: false,
        widthCustom: '9rem',
        minWidthCustom: '9rem'
      },
      {
        Header: 'Type',
        accessor: (row) => convertType(row),
        id: 'requestTypes',
        widthCustom: '6rem',
        minWidthCustom: '6rem'
      },
      {
        Header: 'Proposed equity',
        accessor: (row) => convertEquityPercentage(row),
        id: 'equityPercentage',
        isSortField: true,
        widthCustom: '9rem',
        minWidthCustom: '9rem'
      },
      {
        Header: 'Status',
        accessor: (row) => convertStatus(row),
        id: 'status',
        widthCustom: '6rem',
        minWidthCustom: '6rem'
      },
      {
        Header: 'Date Received',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.createdAt, true)}</Text>,
        id: 'createdAt',
        widthCustom: '9rem',
        minWidthCustom: '9rem',
        isSortField: true
      },
      {
        Header: 'Actions',
        accessor: (row) => convertAction(row),
        widthCustom: '8rem',
        minWidthCustom: '8rem',
        id: 'action'
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertResponses, convertType, convertStatus, convertAction, handleSort]
  );

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={'12px'}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        minH={'72px'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          flexDir={{ base: 'column', md: 'row' }}
          gap={'12px'}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search responses'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
          <div className="flex items-center gap-3">
            {/* Equity Percentage Menu List */}
            <div ref={menuRef} className="w-full">
              <Menu isOpen={isOpen} onClose={() => {}}>
                <MenuButton
                  as={Button}
                  onClick={onOpen}
                  rightIcon={
                    <div className="ml-[16px]">
                      <img src={ChevronDownIcon} alt="icon" />
                    </div>
                  }
                  w={'full'}
                  height={'36px'}
                  variant={'growth-green-solid'}
                  px={'18px'}
                >
                  <Text color={'#4CAF50'} fontWeight={900} textAlign={'start'} fontSize={'sm'}>
                    {equityPercentage.name || 'Equity percentage'}
                  </Text>
                </MenuButton>
                <MenuList
                  p={'0.5rem'}
                  borderRadius={'0.5rem'}
                  minW={'205px'}
                  shadow={'md'}
                  border={'1px solid'}
                  borderColor={'light-gray'}
                  maxH={'300px'}
                  overflow={'auto'}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#e6e7eb',
                      borderRadius: '30px'
                    }
                  }}
                >
                  {listEquityPercentage?.map((item, index) => {
                    return (
                      <>
                        <MenuItem
                          key={index}
                          minW={'114px'}
                          onClick={handleChangeEquityPercentage(item)}
                          p={'8px 8px 8px 10px'}
                          _focus={{ bgColor: 'white' }}
                          _hover={{
                            bgColor: 'anti-flash-white',
                            '& .child-box': {
                              display: 'block'
                            }
                          }}
                          rounded={'0.5rem'}
                          fontSize={'14px'}
                          lineHeight={'20px'}
                          fontWeight={'800'}
                          hidden={item.id === undefined ? true : false}
                          display={'block'}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              className="w-5 h-5 mr-2 custom-radio-green"
                              checked={equityPercentage?.id === item.id}
                              onChange={(e) => e.stopPropagation()}
                            />
                            <p>{item.name}</p>
                          </div>
                        </MenuItem>
                        {item.id === 6 ? (
                          <div
                            hidden={equityPercentage.id !== 6 ? true : false}
                            id="custom-range-field"
                            className="grid grid-cols-2 ml-[28px] gap-x-[16px]"
                          >
                            <p className="font-[400]">Min</p>
                            <p className="font-[400]">Max</p>
                            <Input
                              placeholder={'Enter %'}
                              padding={'8px'}
                              height={'41px'}
                              width={'63.5px'}
                              type="number"
                              sx={{
                                '::placeholder': {
                                  fontSize: '12px',
                                  color: 'gray.500'
                                }
                              }}
                              onInput={(e) => {
                                setMinEquityPercentage(e.target.value);
                              }}
                              value={minEquityPercentage === undefined ? '' : minEquityPercentage}
                            />
                            <Input
                              placeholder={'Enter %'}
                              padding={'8px'}
                              height={'41px'}
                              width={'63.5px'}
                              type="number"
                              sx={{
                                '::placeholder': {
                                  fontSize: '12px',
                                  color: 'gray.500'
                                }
                              }}
                              onInput={(e) => {
                                setMaxEquityPercentage(e.target.value);
                              }}
                              value={maxEquityPercentage === undefined ? '' : maxEquityPercentage}
                            />
                          </div>
                        ) : (
                          <></>
                        )}
                      </>
                    );
                  })}
                </MenuList>
              </Menu>
            </div>

            {/* Type Menu List */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={
                  <div className="ml-[16px]">
                    <img src={ChevronDownIcon} alt="icon" />
                  </div>
                }
                height={'36px'}
                w={'full'}
                variant={'growth-green-solid'}
                px={{ base: '10px', md: '18px' }}
              >
                <Text color={'#4CAF50'} fontWeight={900} textAlign={'start'} fontSize={'sm'}>
                  {type.name || 'Type'}
                </Text>
              </MenuButton>
              <MenuList
                p={'0.5rem'}
                borderRadius={'0.5rem'}
                minW={'130px'}
                shadow={'md'}
                border={'1px solid'}
                borderColor={'light-gray'}
                maxH={'300px'}
                overflow={'auto'}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e6e7eb',
                    borderRadius: '30px'
                  }
                }}
              >
                {[{ id: undefined, name: 'All Types' }, ...(getListTopic || [])]?.map(
                  (item, index) => {
                    return (
                      <MenuItem
                        key={index}
                        minW={'114px'}
                        onClick={handleChangeType(item)}
                        p={'8px 8px 8px 10px'}
                        _focus={{ bgColor: 'white' }}
                        _hover={{
                          bgColor: 'anti-flash-white',
                          '& .child-box': {
                            display: 'block'
                          }
                        }}
                        rounded={'0.5rem'}
                        fontSize={'14px'}
                        lineHeight={'20px'}
                        fontWeight={'800'}
                      >
                        {item.name}
                      </MenuItem>
                    );
                  }
                )}
              </MenuList>
            </Menu>
          </div>
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'400px'}
        flexFlow={'column'}
        overflow={{ base: 'auto', md: 'auto' }}
        css={{
          '&::-webkit-scrollbar': {
            height: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable ?? []}
          headerHeight={'46px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'380px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven’t received any submissions yet."
              description="Your request is still waiting for submissions from the community. Check back later or try sharing your request to attract more contributors."
              shareUrl={`${process.env.REACT_APP_NEWSLETTER_URL}request/${requestId}/detail`}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'380px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>
      {getListResponses?.totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={getListResponses?.totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
      {modals.find((modal) => modal === MODAL.VIEW_DETAIL) ? (
        <ViewResponseDetailModal
          isOpen
          onClose={() => {
            handleCloseModal(MODAL.VIEW_DETAIL);
          }}
          onAccept={() => {
            handleShowApprovementModal(selectedResponse);
          }}
          onReject={() => {
            handleShowRejectionModal(selectedResponse);
          }}
          data={selectedResponse}
        />
      ) : null}
      {modals.find((modal) => modal === MODAL.APPROVEMENT) ? (
        <ConfirmModal
          isOpen={true}
          title={'Accept Response?'}
          desc={
            'Accepting this response will allow the contributor to gain shared equity if you use their content in your articles.'
          }
          textAlign={'left'}
          titleBtn={'Confirm acceptance'}
          onClick={() => {
            handleApproveResponse(selectedResponse?.id);
          }}
          isLoading={false}
          onClose={() => {
            handleCloseModal(MODAL.APPROVEMENT);
          }}
          type="approve"
        />
      ) : null}
      {modals.find((modal) => modal === MODAL.REJECTION) ? (
        <ConfirmModal
          isOpen={true}
          title={'Reject Response?'}
          textAlign={'left'}
          desc={
            'Rejecting this response will prevent you from viewing its details again, and the contributor will be notified. This action cannot be undone.'
          }
          titleBtn={'Confirm rejection'}
          onClick={() => {
            handleRejectResponse(selectedResponse?.id);
          }}
          isLoading={false}
          onClose={() => {
            handleCloseModal(MODAL.REJECTION);
          }}
          type="reject"
        />
      ) : null}
    </Box>
  );
};
